import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { EVENTS } from '@/analytics/consts';
import { useAnalytics } from '@/analytics/useAnalytics';
import {
  getGetAskMeAnythingChatHistoryQueryKey,
  getListAgentThreadsQueryKey,
  ThreadResponse,
  ThreadDto,
  ChatMessageDtoType,
  AgentChatHistoryResponse
} from '@/api/generated';
import { HumanMessageContainer } from '@/components/AskMeAnything/ChatStyle';
import { useChatStream } from '@/components/AskMeAnything/hooks/useChatStream';
import { DEFAULT_THREAD_ID, DEFAULT_CHAT_PROMPT } from '@/components/AskMeAnything/types';
import { useChatHistory } from '@/hooks/askMeAnything/useChatHistory';
import useThreads from '@/hooks/askMeAnything/useThreads';
import {
  useChatPrompt,
  useActiveThreadId,
  useIsThreadNew,
  useChatActions,
  useIsStreaming
} from '@/store/chat/chatStore';

import ChatSkeleton from './ChatSkeleton';
import JamieReply from './jamieReply/JamieReply';

interface ChatResponseProps {
  setChatTitle: (title: string) => void;
  activeThread: ThreadDto | null;
  style?: React.CSSProperties;
}

const ChatResponse = ({ setChatTitle, activeThread, style }: ChatResponseProps) => {
  const activeThreadId = useActiveThreadId();
  const isThreadNew = useIsThreadNew();
  const { setIsThreadNew } = useChatActions();
  const queryClient = useQueryClient();
  const threadsQueryKey = getListAgentThreadsQueryKey();
  const queryKey = getGetAskMeAnythingChatHistoryQueryKey(activeThreadId);
  const { generateThreadTitleAsync, patchThread } = useThreads();
  const { captureEvent } = useAnalytics();
  const isStreaming = useIsStreaming();
  const {
    streamingResponse,
    completeMessage,
    startStream,
    cancelStream,
    isDone,
    streamingThoughts
  } = useChatStream();

  const chatWrapperRef = useRef<HTMLDivElement | null>(null);
  const chatContentRef = useRef<HTMLDivElement | null>(null);
  const lastHumanMessageRef = useRef<HTMLDivElement | null>(null);
  const lastPromptRef = useRef<string | null>(null);
  const lastThreadIdRef = useRef<string>(DEFAULT_THREAD_ID);
  const chatPrompt = useChatPrompt();

  const { chatHistory, isFetchingChatHistory, isErrorChatHistory } = useChatHistory(
    activeThreadId,
    activeThreadId !== lastThreadIdRef.current && !isThreadNew
  );

  const localChatHistory = useMemo(() => {
    if (!activeThread?.isUserCreated) {
      if (chatHistory[0]?.type === ChatMessageDtoType.human) {
        return chatHistory.slice(1);
      } else {
        return chatHistory;
      }
    }
    return chatHistory;
  }, [chatHistory, activeThread]);

  useEffect(() => {
    if (lastThreadIdRef.current === activeThreadId) return;
    lastThreadIdRef.current = activeThreadId;
  }, [activeThreadId]);

  useLayoutEffect(() => {
    if (chatPrompt === lastPromptRef.current) {
      lastPromptRef.current = null;
      return;
    }
    lastPromptRef.current = chatPrompt;
    if (chatPrompt !== null && chatPrompt !== DEFAULT_CHAT_PROMPT) {
      const newMessage = {
        type: 'human',
        message: chatPrompt
      };
      queryClient.setQueryData(queryKey, (oldData: AgentChatHistoryResponse) => {
        if (!oldData) return oldData;
        const currentMessages = oldData.items?.[0]?.messages ?? [];
        return {
          ...oldData,
          items: [
            {
              ...oldData.items?.[0],
              messages: [...currentMessages, newMessage]
            }
          ]
        };
      });

      startStream(chatPrompt, activeThreadId);

      requestAnimationFrame(() => {
        const wrapper = chatWrapperRef.current;
        const content = chatContentRef.current;
        const lastHuman = lastHumanMessageRef.current;

        if (!wrapper || !content || !lastHuman) return;

        const wrapperHeight = wrapper.clientHeight;
        const messageOffset = lastHuman.offsetTop;

        const requiredContentHeight = messageOffset + wrapperHeight;

        // Grow the content height if it's not tall enough
        if (content.offsetHeight < requiredContentHeight) {
          content.style.height = `${requiredContentHeight - 10}px`;
        }

        // Scroll so the human message aligns with top
        lastHuman.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      });
    }
  }, [chatPrompt]);

  useEffect(() => {
    if (isDone && completeMessage) {
      captureEvent(EVENTS.CHAT_MESSAGE_RECEIVED);
      const newMessage = {
        type: 'ai',
        message: completeMessage
      };
      queryClient.setQueryData(queryKey, (oldData: AgentChatHistoryResponse) => {
        if (!oldData) return oldData;
        const currentMessages = oldData.items?.[0]?.messages ?? [];
        return {
          ...oldData,
          items: [
            {
              ...oldData.items?.[0],
              messages: [...currentMessages, newMessage]
            }
          ]
        };
      });
      cancelStream();
    }
  }, [isDone, completeMessage]);

  useEffect(() => {
    chatWrapperRef.current?.scrollTo({ top: 0 });
  }, [activeThreadId]);

  const createThreadTitle = useCallback(async () => {
    generateThreadTitleAsync({ id: activeThreadId }).then(title => {
      patchThread(
        { id: activeThreadId, data: { title: title.items?.[0]?.title || '' } },
        {
          onSuccess: () => {
            queryClient.setQueryData(threadsQueryKey, (oldData: ThreadResponse) => {
              if (!oldData) return oldData;
              return {
                ...oldData,
                items: oldData.items?.map((thread: ThreadDto) =>
                  thread.id === activeThreadId
                    ? { ...thread, title: title.items?.[0]?.title || '' }
                    : thread
                )
              };
            });
            setIsThreadNew(false);
            setChatTitle(title.items?.[0]?.title || '');
          }
        }
      );
    });
  }, [activeThreadId]);

  useEffect(() => {
    if (!isThreadNew) return;
    const hasBotHistory = chatHistory?.some(message => message.type === ChatMessageDtoType.ai);
    if (!hasBotHistory) return;
    createThreadTitle();
  }, [isThreadNew, chatHistory, createThreadTitle]);

  return (
    <div style={{ overflow: 'auto', height: '100%', ...style }} ref={chatWrapperRef}>
      <div style={{ padding: '16px', height: '100%' }} ref={chatContentRef}>
        {!isFetchingChatHistory ? (
          <>
            {localChatHistory?.map((message, index) => {
              const isLastHuman =
                message.type === ChatMessageDtoType.human &&
                localChatHistory.findLastIndex(m => m.type === ChatMessageDtoType.human) === index;
              if (message.type === ChatMessageDtoType.ai) {
                return (
                  message.response && (
                    <JamieReply
                      key={index}
                      response={message.response}
                      thoughts={[message.thoughts]}
                      isStreamingThoughts={false}
                    />
                  )
                );
              } else {
                return (
                  <HumanMessageContainer key={index} ref={isLastHuman ? lastHumanMessageRef : null}>
                    <Typography className="message">{message.response}</Typography>
                  </HumanMessageContainer>
                );
              }
            })}
            {(streamingResponse || isStreaming || streamingThoughts.length > 0) && (
              <JamieReply
                response={streamingResponse}
                thoughts={streamingThoughts}
                isStreamingThoughts={streamingThoughts.length > 0}
              />
            )}
          </>
        ) : (
          <ChatSkeleton />
        )}
      </div>
    </div>
  );
};
export default ChatResponse;
